import { useEffect, useMemo, useState } from "react"
import { Alert, Button, Form, Input } from "antd"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { LockOutlined } from "@ant-design/icons"
import AuthShell from "../../Layout/AuthShell"
import apiClient from "../../Utils/apiClient"
import generateStrongPassword from "../../Utils/generateStrongPassword"
import { productName } from "../../config"
import { rememberTenant } from "../../Utils/tenantStorage"

const OnboardOrganization = () => {
  const [searchParams] = useSearchParams()
  const token = useMemo(() => searchParams.get("token") || "", [searchParams])
  const tenant = useMemo(() => searchParams.get("tenant") || "", [searchParams])
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [invite, setInvite] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [done, setDone] = useState(null)

  useEffect(() => {
    if (tenant) rememberTenant(tenant)
    if (!token) {
      setLoading(false)
      setError("This setup link is missing or incomplete.")
      return undefined
    }
    let cancelled = false
    const load = async () => {
      try {
        const res = await apiClient("platform/onboard", "GET", null, {
          params: { token, tenant },
          refresh: false,
        })
        if (cancelled) return
        if (res?.success) setInvite(res.data)
        else setError(res?.message || "This setup link is invalid or has expired.")
      } catch (err) {
        if (!cancelled) setError(err?.message || "This setup link is invalid or has expired.")
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [token, tenant])

  const onFinish = async (values) => {
    setSaving(true)
    setError("")
    try {
      const res = await apiClient(
        "platform/onboard",
        "POST",
        {
          token,
          tenantSlug: invite?.slug || tenant,
          password: values.password,
          passwordConfirm: values.passwordConfirm,
        },
        { refresh: false }
      )
      if (!res?.success) {
        setError(res?.message || "Could not finish setup.")
        return
      }
      rememberTenant(res.data?.slug || tenant)
      setDone(res.data)
    } catch (err) {
      setError(err?.message || "Could not finish setup.")
    } finally {
      setSaving(false)
    }
  }

  if (done) {
    return (
      <AuthShell
        title={`${done.organization} is ready`}
        subtitle={`Sign in to ${productName} with organization ${done.slug} and ${done.email}.`}
        badge="Organization setup"
        steps={["Invite", "Password", "Console"]}
        activeStep={3}
      >
        <Alert
          type="success"
          showIcon
          className="mb-3"
          message="Administrator account created"
          description="Two-factor verification is required the first time you sign in."
        />
        <Button
          type="primary"
          block
          size="large"
          onClick={() =>
            navigate("/login", {
              replace: true,
              state: { passwordReset: true },
            })
          }
        >
          Sign in
        </Button>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title={invite ? `Set up ${invite.organization}` : "Organization setup"}
      subtitle={
        invite
          ? `${invite.adminName}, choose a password for ${invite.email}. You will sign in with organization ${invite.slug}.`
          : "Open the link from your invitation email."
      }
      badge="Organization setup"
      steps={["Invite", "Password", "Console"]}
      activeStep={2}
      footer={<Link to="/login">Already set up? Sign in</Link>}
    >
      {loading ? <p>Checking your invitation…</p> : null}
      {error ? (
        <Alert type="error" showIcon className="mb-3" message={error} />
      ) : null}
      {invite ? (
        <Form form={form} layout="vertical" requiredMark={false} onFinish={onFinish}>
          <Form.Item
            label="Password"
            name="password"
            rules={[
              { required: true, message: "Choose a password" },
              { min: 8, message: "Use at least 8 characters" },
            ]}
          >
            <Input.Password
              size="large"
              prefix={<LockOutlined />}
              placeholder="At least 8 characters"
            />
          </Form.Item>
          <Form.Item
            label="Confirm password"
            name="passwordConfirm"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Confirm your password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) return Promise.resolve()
                  return Promise.reject(new Error("Passwords do not match"))
                },
              }),
            ]}
          >
            <Input.Password size="large" prefix={<LockOutlined />} />
          </Form.Item>
          <Button
            className="mb-2"
            onClick={() => {
              const password = generateStrongPassword(12)
              form.setFieldsValue({ password, passwordConfirm: password })
            }}
          >
            Generate a password
          </Button>
          <Form.Item className="mb-0">
            <Button type="primary" htmlType="submit" block size="large" loading={saving}>
              Create administrator account
            </Button>
          </Form.Item>
        </Form>
      ) : null}
    </AuthShell>
  )
}

export default OnboardOrganization
